import { useEffect, useMemo, useState } from "react";
import { FileCheck2, History, Printer, Search, ShieldCheck, X } from "lucide-react";
import {
  confirmStatementPrintOutcome,
  downloadStatementPrintReport,
  getStatementPrintDetail,
  getStatementPrintReports,
  listStatementPrintQueue,
  prepareStatementPrint,
  recordStatementPrintDialog,
  statementPrintPdfUrl,
  type StatementPrintDetail,
  type StatementPrintPermissions,
  type StatementPrintPreparation,
  type StatementPrintQueueItem,
  type StatementPrintVersion,
} from "../../lib/arRecoveryStatementPrintApi";

type StatementPrintWorkspaceProps = {
  onClose: () => void;
  onOpenSend?: (statementId: string) => void;
  initialStatementId?: string;
  initialClient?: string;
  initialCustomer?: string;
  initialAccount?: string;
  initialRecipient?: string;
  sourceWorkspace?: string;
};

const EMPTY_PERMISSIONS: StatementPrintPermissions = {
  canView: false,
  canPrepare: false,
  canPrintLatest: false,
  canPrintHistorical: false,
  canCreatePackage: false,
  canOverrideAddress: false,
  canConfirmOutcome: false,
  canViewHistory: false,
  canViewAudit: false,
  canExportReport: false,
};

const FIELD = "focus-ring min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2.5 text-sm font-semibold text-[#0A2540]";

export function ARRecoveryStatementPrintWorkspace({
  onClose,
  onOpenSend,
  initialStatementId = "",
  initialClient = "",
  initialCustomer = "",
  initialAccount = "",
  initialRecipient = "",
  sourceWorkspace = "Statement Center",
}: StatementPrintWorkspaceProps) {
  const [search, setSearch] = useState(initialStatementId);
  const [client, setClient] = useState(initialClient);
  const [customer, setCustomer] = useState(initialCustomer);
  const [account, setAccount] = useState(initialAccount);
  const [recipient, setRecipient] = useState(initialRecipient);
  const [statementStatus, setStatementStatus] = useState("");
  const [versionStatus, setVersionStatus] = useState("");
  const [generatedDate, setGeneratedDate] = useState("");
  const [statementDate, setStatementDate] = useState("");
  const [recipientStatus, setRecipientStatus] = useState("");
  const [addressReadiness, setAddressReadiness] = useState("");
  const [integrityStatus, setIntegrityStatus] = useState("");
  const [printStatus, setPrintStatus] = useState("");
  const [lastPrintedDate, setLastPrintedDate] = useState("");
  const [sort, setSort] = useState("generated-desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1, hasNext: false, hasPrevious: false });
  const [items, setItems] = useState<StatementPrintQueueItem[]>([]);
  const [selectedStatementId, setSelectedStatementId] = useState(initialStatementId);
  const [detail, setDetail] = useState<StatementPrintDetail | null>(null);
  const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS);
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [copies, setCopies] = useState(1);
  const [paperSize, setPaperSize] = useState("US Letter");
  const [orientation, setOrientation] = useState("Portrait");
  const [colorPreference, setColorPreference] = useState("Printer default");
  const [sidesPreference, setSidesPreference] = useState("Printer default");
  const [manualOverride, setManualOverride] = useState(false);
  const [manualAddress, setManualAddress] = useState({ name: "", company: "", addressLine1: "", addressLine2: "", city: "", region: "", postalCode: "", country: "United States" });
  const [activePreparation, setActivePreparation] = useState<StatementPrintPreparation | null>(null);
  const [outcome, setOutcome] = useState("PRINT_CONFIRMED");
  const [outcomeReason, setOutcomeReason] = useState("");
  const [reprintPreparationId, setReprintPreparationId] = useState("");
  const [reprintReason, setReprintReason] = useState("");
  const [reports, setReports] = useState<Awaited<ReturnType<typeof getStatementPrintReports>>["reports"] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    setPage(1);
  }, [search, client, customer, account, recipient, statementStatus, versionStatus, generatedDate, statementDate, recipientStatus, addressReadiness, integrityStatus, printStatus, lastPrintedDate, sort]);

  useEffect(() => {
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      setIsLoading(true);
      setError("");
      listStatementPrintQueue({
        search, client, customer, account, recipient, statementStatus, versionStatus, generatedDate,
        statementDate, recipientStatus, addressReadiness, integrityStatus, printStatus, lastPrintedDate,
        sort, page, pageSize,
      }).then((payload) => {
        if (cancelled) return;
        setItems(payload.items);
        setPermissions(payload.permissions);
        setPagination(payload.pagination);
        setSelectedStatementId((current) => current && payload.items.some((item) => item.statementId === current) ? current : payload.items[0]?.statementId || "");
      }).catch((nextError) => {
        if (!cancelled) {
          setItems([]);
          setError(nextError instanceof Error ? nextError.message : "Statement Print queue could not be loaded.");
        }
      }).finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [search, client, customer, account, recipient, statementStatus, versionStatus, generatedDate, statementDate, recipientStatus, addressReadiness, integrityStatus, printStatus, lastPrintedDate, sort, page, pageSize]);

  useEffect(() => {
    if (!selectedStatementId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setIsDetailLoading(true);
    setActivePreparation(null);
    getStatementPrintDetail(selectedStatementId).then((payload) => {
      if (cancelled) return;
      setDetail(payload);
      setPermissions(payload.permissions);
      const active = payload.versions.find((version) => version.isActive) || payload.versions[0];
      setSelectedVersionId(active?.documentVersionId || "");
      setManualAddress({
        name: payload.address.name,
        company: payload.address.company,
        addressLine1: payload.address.addressLine1,
        addressLine2: payload.address.addressLine2,
        city: payload.address.city,
        region: payload.address.region,
        postalCode: payload.address.postalCode,
        country: payload.address.country,
      });
    }).catch((nextError) => {
      if (!cancelled) setError(nextError instanceof Error ? nextError.message : "Statement Print detail could not be loaded.");
    }).finally(() => {
      if (!cancelled) setIsDetailLoading(false);
    });
    return () => { cancelled = true; };
  }, [selectedStatementId]);

  useEffect(() => {
    if (!permissions.canViewHistory) return;
    getStatementPrintReports().then((payload) => setReports(payload.reports)).catch(() => setReports(null));
  }, [permissions.canViewHistory]);

  const selectedVersion = useMemo(
    () => detail?.versions.find((version) => version.documentVersionId === selectedVersionId) || null,
    [detail, selectedVersionId],
  );

  async function refreshDetail() {
    if (!selectedStatementId) return;
    const payload = await getStatementPrintDetail(selectedStatementId);
    setDetail(payload);
    const queue = await listStatementPrintQueue({ search, client, customer, account, recipient, statementStatus, versionStatus, generatedDate, statementDate, recipientStatus, addressReadiness, integrityStatus, printStatus, lastPrintedDate, sort, page, pageSize });
    setItems(queue.items);
    setPagination(queue.pagination);
    if (permissions.canViewHistory) setReports((await getStatementPrintReports()).reports);
  }

  async function openPrintDialog() {
    if (!detail || !selectedVersion || isBusy) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      setError("The print window was blocked. Allow popups for this site and try again.");
      return;
    }
    printWindow.opener = null;
    printWindow.document.write("<title>Preparing Statement Print</title><p style='font:600 16px sans-serif;padding:32px'>Verifying the immutable Statement PDF...</p>");
    setIsBusy(true);
    setError("");
    setMessage("");
    try {
      const payload = await prepareStatementPrint(
        detail.statement.statementId,
        selectedVersion.documentVersionId,
        {
          copies, paperSize, orientation, colorPreference, sidesPreference,
          includeCoverSheet: false, includeAddressPage: false, includeControlPage: false,
          manualAddress: manualOverride ? manualAddress : null,
          reprintReason: reprintPreparationId ? reprintReason : undefined,
          priorPrintPreparationId: reprintPreparationId || undefined,
        },
        sourceWorkspace,
      );
      setActivePreparation(payload.preparation);
      await recordStatementPrintDialog(detail.statement.statementId, payload.preparation.preparationId);
      printWindow.location.href = statementPrintPdfUrl(detail.statement.statementId, payload.preparation.preparationId);
      window.setTimeout(() => {
        try { printWindow.focus(); printWindow.print(); } catch { /* Browser PDF viewers may require the user to select Print. */ }
      }, 900);
      setMessage("Print dialog opened. Confirm the actual outcome below after the browser or printer operation finishes.");
      await refreshDetail();
    } catch (nextError) {
      printWindow.close();
      setError(nextError instanceof Error ? nextError.message : "Statement print preparation failed.");
    } finally {
      setIsBusy(false);
    }
  }

  async function confirmOutcome() {
    if (!detail || !activePreparation || isBusy) return;
    setIsBusy(true);
    setError("");
    try {
      const result = await confirmStatementPrintOutcome(detail.statement.statementId, activePreparation.preparationId, outcome, outcomeReason);
      setActivePreparation(result.preparation);
      setMessage(`Print outcome recorded as ${humanize(outcome)}.`);
      await refreshDetail();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Print outcome could not be recorded.");
    } finally {
      setIsBusy(false);
    }
  }

  async function exportReport() {
    try {
      const result = await downloadStatementPrintReport();
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Print report export failed.");
    }
  }

  function clearFilters() {
    setSearch(""); setClient(""); setCustomer(""); setAccount(""); setRecipient(""); setStatementStatus("");
    setVersionStatus(""); setGeneratedDate(""); setStatementDate(""); setRecipientStatus(""); setAddressReadiness("");
    setIntegrityStatus(""); setPrintStatus(""); setLastPrintedDate(""); setSort("generated-desc"); setPage(1);
  }

  return (
    <div className="mt-3 grid min-w-0 max-w-full gap-3 overflow-hidden text-[#0A2540]" data-statement-print-workspace="canonical-r2">
      <section className="min-w-0 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Operations</p>
            <h1 className="mt-1 font-display text-[1.65rem] font-bold leading-tight">Statement Print</h1>
            <p className="mt-1 max-w-4xl text-sm font-semibold leading-5 text-[#475569]">
              Prepare an authorized immutable PDF, review print instructions, open the system print dialog, then explicitly record the operational outcome.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {onOpenSend && selectedStatementId ? (
              <button type="button" onClick={() => onOpenSend(selectedStatementId)} className="focus-ring inline-flex items-center gap-2 rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-white">
                Send Now
              </button>
            ) : null}
            <button type="button" onClick={onClose} className="focus-ring inline-flex items-center gap-2 rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.12em]">
              <X className="size-4" aria-hidden="true" /> Close
            </button>
          </div>
        </div>
      </section>

      <section className="grid min-w-0 gap-2.5 sm:grid-cols-2 xl:grid-cols-5">
        <Metric label="Print-ready queue" value={pagination.total} />
        <Metric label="Confirmed prints" value={reports?.summary.confirmed || 0} />
        <Metric label="Reprints" value={reports?.summary.reprints || 0} />
        <Metric label="Cancelled" value={reports?.summary.cancelled || 0} />
        <Metric label="Exceptions" value={reports?.summary.failed || 0} />
      </section>

      <section className="grid min-w-0 gap-2.5 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-3.5 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
        <div className="grid min-w-0 gap-2 lg:grid-cols-[minmax(16rem,2fr)_repeat(4,minmax(8rem,1fr))]">
          <label className="relative min-w-0">
            <span className="sr-only">Search Statement Print queue</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#14B8A6]" aria-hidden="true" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Statement, customer, account, placement, client, recipient, address" className={`${FIELD} w-full pl-10`} />
          </label>
          <input value={client} onChange={(event) => setClient(event.target.value)} placeholder="Client" aria-label="Filter print queue by client" className={FIELD} />
          <input value={customer} onChange={(event) => setCustomer(event.target.value)} placeholder="Customer" aria-label="Filter print queue by customer" className={FIELD} />
          <input value={account} onChange={(event) => setAccount(event.target.value)} placeholder="Account / placement" aria-label="Filter print queue by account or placement" className={FIELD} />
          <input value={recipient} onChange={(event) => setRecipient(event.target.value)} placeholder="Recipient" aria-label="Filter print queue by recipient" className={FIELD} />
        </div>
        <div className="grid min-w-0 gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <input value={statementStatus} onChange={(event) => setStatementStatus(event.target.value)} placeholder="Statement status" aria-label="Filter by Statement status" className={FIELD} />
          <input value={versionStatus} onChange={(event) => setVersionStatus(event.target.value)} placeholder="Version status" aria-label="Filter by version status" className={FIELD} />
          <select value={addressReadiness} onChange={(event) => setAddressReadiness(event.target.value)} aria-label="Filter by address readiness" className={FIELD}>
            <option value="">All address states</option><option>Ready</option><option>Not Ready</option>
          </select>
          <select value={integrityStatus} onChange={(event) => setIntegrityStatus(event.target.value)} aria-label="Filter by integrity status" className={FIELD}>
            <option value="">All integrity states</option><option>Ready</option><option>Not Ready</option>
          </select>
          <select value={printStatus} onChange={(event) => setPrintStatus(event.target.value)} aria-label="Filter by print status" className={FIELD}>
            <option value="">All print states</option><option>Never Printed</option><option>Previously Printed</option><option value="PRINT_CONFIRMED">Print confirmed</option><option value="PRINT_FAILED">Print failed</option>
          </select>
        </div>
        <div className="grid min-w-0 gap-2 sm:grid-cols-2 lg:grid-cols-[repeat(5,minmax(8rem,1fr))_auto]">
          <label className="grid gap-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#64748B]">Generated date<input type="date" value={generatedDate} onChange={(event) => setGeneratedDate(event.target.value)} className={FIELD} /></label>
          <label className="grid gap-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#64748B]">Statement date<input type="date" value={statementDate} onChange={(event) => setStatementDate(event.target.value)} className={FIELD} /></label>
          <label className="grid gap-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#64748B]">Last printed<input type="date" value={lastPrintedDate} onChange={(event) => setLastPrintedDate(event.target.value)} className={FIELD} /></label>
          <input value={recipientStatus} onChange={(event) => setRecipientStatus(event.target.value)} placeholder="Recipient status" aria-label="Filter by recipient status" className={`${FIELD} self-end`} />
          <select value={sort} onChange={(event) => setSort(event.target.value)} aria-label="Sort Statement Print queue" className={`${FIELD} self-end`}>
            <option value="generated-desc">Generated, newest</option><option value="statement-number-asc">Statement number</option>
            <option value="customer-asc">Customer</option><option value="statement-date-desc">Statement date</option>
            <option value="balance-desc">Balance</option><option value="version-desc">Version</option>
            <option value="last-printed-desc">Last printed</option><option value="print-count-desc">Print count</option>
          </select>
          <button type="button" onClick={clearFilters} className="focus-ring self-end rounded-xl border border-[#BFEAFF] px-4 py-2.5 text-xs font-black uppercase tracking-[0.1em]">Clear</button>
        </div>
      </section>

      {error ? <p role="alert" className="rounded-xl border border-[#FECACA] bg-[#FFF7F7] px-4 py-3 text-sm font-bold text-[#991B1B]">{error}</p> : null}
      {message ? <p role="status" className="rounded-xl border border-[#99F6E4] bg-[#F0FDFA] px-4 py-3 text-sm font-bold text-[#115E59]">{message}</p> : null}

      <section className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1.4fr)]">
        <div className="min-w-0 overflow-hidden rounded-[1.35rem] border border-[#BFEAFF] bg-white shadow-[0_12px_30px_rgba(10,37,64,.07)]">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#D9F0FF] px-4 py-3">
            <div><p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Print Queue</p><p className="text-sm font-bold">{pagination.total.toLocaleString()} eligible records</p></div>
            <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }} aria-label="Print queue page size" className={FIELD}>
              <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
            </select>
          </div>
          <div className="max-h-[40rem] overflow-y-auto overscroll-contain">
            {isLoading ? <p className="p-4 text-sm font-semibold text-[#64748B]">Loading print queue...</p> : items.map((item) => {
              const selected = item.statementId === selectedStatementId;
              return (
                <button key={item.statementId} type="button" onClick={() => setSelectedStatementId(item.statementId)} className={`focus-ring grid w-full min-w-0 gap-2 border-b border-[#E2F4FF] px-4 py-3 text-left transition ${selected ? "bg-[#E6FFFB]" : "bg-white hover:bg-[#F4FBFF]"}`}>
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <div className="min-w-0"><p className="break-words text-sm font-black">{item.statementNumber}</p><p className="break-words text-xs font-semibold text-[#475569]">{item.customerName} · {item.accountId}</p></div>
                    <span className="shrink-0 rounded-full border border-[#BFEAFF] px-2 py-1 text-[10px] font-black uppercase tracking-[0.1em]">{item.printStatus.replaceAll("_", " ")}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-[#475569] sm:grid-cols-4">
                    <span>v{item.versionNumber || "—"} · {item.documentStatus}</span><span>{formatMoney(item.balanceCents)}</span>
                    <span>Address: {item.addressReadiness}</span><span>Printed: {item.printCount}</span>
                  </div>
                </button>
              );
            })}
            {!isLoading && !items.length ? <p className="p-4 text-sm font-semibold text-[#64748B]">No Statements match the active Print filters.</p> : null}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
            <button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={!pagination.hasPrevious} className="focus-ring rounded-full border border-[#BFEAFF] px-3 py-2 text-xs font-black disabled:opacity-40">Previous</button>
            <span className="text-xs font-bold">Page {pagination.page} of {pagination.totalPages}</span>
            <button type="button" onClick={() => setPage((value) => value + 1)} disabled={!pagination.hasNext} className="focus-ring rounded-full border border-[#BFEAFF] px-3 py-2 text-xs font-black disabled:opacity-40">Next</button>
          </div>
        </div>

        <div className="grid min-w-0 content-start gap-3">
          {isDetailLoading ? <Panel title="Print Preparation Detail"><p className="text-sm font-semibold text-[#64748B]">Loading Statement context...</p></Panel> : detail ? (
            <>
              <Panel title="Statement and Version Context" icon={<FileCheck2 className="size-4" aria-hidden="true" />}>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  <Field label="Client" value={detail.statement.clientCompany || "Not captured"} />
                  <Field label="Customer" value={detail.statement.customerName} />
                  <Field label="Account / Placement" value={`${detail.statement.accountId}${detail.statement.placementId ? ` / ${detail.statement.placementId}` : ""}`} />
                  <Field label="Statement" value={detail.statement.statementNumber} />
                  <Field label="Statement date" value={formatDate(detail.statement.statementDate)} />
                  <Field label="Balance" value={formatMoney(detail.statement.balanceCents)} />
                  <Field label="Status" value={detail.statement.lifecycleStatus} />
                  <Field label="Recipient" value={detail.statement.recipientName || detail.statement.recipientEmail || "Not assigned"} />
                </div>
                <label className="mt-3 grid gap-1 text-xs font-black uppercase tracking-[0.12em] text-[#475569]">
                  Version
                  <select value={selectedVersionId} onChange={(event) => setSelectedVersionId(event.target.value)} className={FIELD}>
                    {detail.versions.map((version) => <option key={version.documentVersionId} value={version.documentVersionId}>v{version.versionNumber} · {version.documentStatus}{version.isActive ? " · Latest active" : " · Historical"}</option>)}
                  </select>
                </label>
                {selectedVersion ? <div className="mt-3 grid gap-2 sm:grid-cols-3"><Field label="Generated" value={formatDateTime(selectedVersion.generatedAt)} /><Field label="Document size" value={formatBytes(selectedVersion.fileSize)} /><Field label="Integrity source" value={selectedVersion.storageKeyPresent && selectedVersion.mimeType === "application/pdf" ? "Ready for verification" : "Not ready"} /></div> : null}
              </Panel>

              <Panel title="Recipient and Address Readiness">
                <div className="grid gap-2 sm:grid-cols-2"><Field label="Recipient" value={detail.address.name || "Not captured"} /><Field label="Company" value={detail.address.company || "Not captured"} /><Field label="Address" value={[detail.address.addressLine1, detail.address.addressLine2, detail.address.city, detail.address.region, detail.address.postalCode, detail.address.country].filter(Boolean).join(", ") || "Not captured"} /><Field label="Status" value={`${detail.address.validationStatus} · ${detail.address.source}`} /></div>
                {detail.address.missingFields.length ? <p className="mt-2 text-xs font-bold text-[#9A3412]">Missing: {detail.address.missingFields.join(", ")}</p> : null}
                {permissions.canOverrideAddress ? <label className="mt-3 flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={manualOverride} onChange={(event) => setManualOverride(event.target.checked)} /> Use audited print-only address override</label> : null}
                {manualOverride ? <div className="mt-3 grid gap-2 sm:grid-cols-2">{Object.entries(manualAddress).map(([key, value]) => <input key={key} value={value} onChange={(event) => setManualAddress((current) => ({ ...current, [key]: event.target.value }))} aria-label={humanize(key)} placeholder={humanize(key)} className={FIELD} />)}</div> : null}
              </Panel>

              <Panel title="Print Options" icon={<Printer className="size-4" aria-hidden="true" />}>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  <label className="grid gap-1 text-xs font-black uppercase tracking-[0.1em]">Copies<input type="number" min={1} max={100} value={copies} onChange={(event) => setCopies(Number(event.target.value))} className={FIELD} /></label>
                  <label className="grid gap-1 text-xs font-black uppercase tracking-[0.1em]">Paper size<select value={paperSize} onChange={(event) => setPaperSize(event.target.value)} className={FIELD}><option>US Letter</option><option>Legal</option><option>A4</option></select><span className="normal-case tracking-normal text-[#64748B]">User-selected printer setting</span></label>
                  <label className="grid gap-1 text-xs font-black uppercase tracking-[0.1em]">Orientation<select value={orientation} onChange={(event) => setOrientation(event.target.value)} className={FIELD}><option>Portrait</option><option>Landscape</option></select><span className="normal-case tracking-normal text-[#64748B]">Passed as print preparation</span></label>
                  <label className="grid gap-1 text-xs font-black uppercase tracking-[0.1em]">Color<select value={colorPreference} onChange={(event) => setColorPreference(event.target.value)} className={FIELD}><option>Printer default</option><option>Color</option><option>Grayscale</option></select><span className="normal-case tracking-normal text-[#64748B]">User-selected printer setting</span></label>
                  <label className="grid gap-1 text-xs font-black uppercase tracking-[0.1em]">Sides<select value={sidesPreference} onChange={(event) => setSidesPreference(event.target.value)} className={FIELD}><option>Printer default</option><option>Single-sided</option><option>Double-sided</option></select><span className="normal-case tracking-normal text-[#64748B]">User-selected printer setting</span></label>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <p className="rounded-xl border border-[#BFEAFF] bg-[#F8FDFF] p-3 text-sm font-semibold text-[#475569]">
                    Printing uses the same authorized, checksum-verified canonical Statement PDF. No secondary persistent print document is created.
                  </p>
                </div>
                <p className="mt-3 rounded-xl bg-[#F4FBFF] px-3 py-2 text-xs font-semibold text-[#475569]">Canonical protection: the source PDF and version remain immutable. Temporary packages expire after {detail.temporaryArtifactPolicy.packageTtlHours} hours.</p>
                {reprintPreparationId ? <label className="mt-3 grid gap-1 text-xs font-black uppercase tracking-[0.1em]">Reprint reason<textarea value={reprintReason} onChange={(event) => setReprintReason(event.target.value)} className={`${FIELD} min-h-20 resize-y`} /></label> : null}
                <button type="button" onClick={() => void openPrintDialog()} disabled={!permissions.canPrepare || !selectedVersion || isBusy || Boolean(reprintPreparationId && !reprintReason)} className="focus-ring mt-3 inline-flex min-h-11 items-center gap-2 rounded-full bg-[#14B8A6] px-5 text-xs font-black uppercase tracking-[0.12em] text-white disabled:cursor-not-allowed disabled:opacity-40">
                  <Printer className="size-4" aria-hidden="true" /> {isBusy ? "Preparing..." : reprintPreparationId ? "Prepare Reprint" : "Verify and Open Print Dialog"}
                </button>
              </Panel>

              {activePreparation && !activePreparation.outcome ? (
                <Panel title="Confirm Actual Print Outcome" icon={<ShieldCheck className="size-4" aria-hidden="true" />}>
                  <p className="text-sm font-semibold text-[#475569]">The print dialog was opened. This does not prove a physical print occurred.</p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto]">
                    <select aria-label="Print outcome" value={outcome} onChange={(event) => setOutcome(event.target.value)} className={FIELD}>
                      <option value="PRINT_CONFIRMED">Printed Successfully</option><option value="PRINT_CANCELLED">Cancelled</option>
                      <option value="PRINTER_ERROR">Printer Error</option><option value="PARTIAL_PRINT">Partial Print</option><option value="REPRINT_REQUIRED">Reprint Required</option>
                    </select>
                    <input aria-label="Print outcome reason" value={outcomeReason} onChange={(event) => setOutcomeReason(event.target.value)} placeholder="Reason required for error, partial print, or reprint" className={FIELD} />
                    <button type="button" onClick={() => void confirmOutcome()} disabled={isBusy || (["PRINTER_ERROR", "PARTIAL_PRINT", "REPRINT_REQUIRED"].includes(outcome) && !outcomeReason.trim())} className="focus-ring rounded-xl bg-[#0A2540] px-4 py-2.5 text-xs font-black uppercase tracking-[0.1em] text-white disabled:opacity-40">Record Outcome</button>
                  </div>
                </Panel>
              ) : null}

              <Panel title="Print History" icon={<History className="size-4" aria-hidden="true" />}>
                <div className="grid gap-2">{detail.history.slice(0, 12).map((entry) => <div key={entry.preparationId} className="grid gap-2 rounded-xl border border-[#D9F0FF] p-3 sm:grid-cols-[1fr_auto]"><div><p className="text-sm font-black">v{entry.versionNumber} · {humanize(entry.outcome || entry.status)}</p><p className="text-xs font-semibold text-[#64748B]">{formatDateTime(entry.updatedAt)} · {entry.actorName || "Unknown user"} · {entry.copies} copies</p><p className="mt-1 break-all font-mono text-[10px] text-[#64748B]">Correlation: {entry.correlationId}</p></div><button type="button" onClick={() => { setSelectedVersionId(entry.documentVersionId); setReprintPreparationId(entry.preparationId); setReprintReason(""); }} className="focus-ring self-start rounded-full border border-[#14B8A6] px-3 py-2 text-xs font-black text-[#0F766E]">Reprint</button></div>)}</div>
                {!detail.history.length ? <p className="text-sm font-semibold text-[#64748B]">No print history exists for this Statement.</p> : null}
              </Panel>

              {permissions.canViewAudit ? <Panel title="Print Audit History"><div className="grid gap-2">{detail.audit.slice(0, 16).map((entry) => <div key={entry.auditId} className="rounded-xl border border-[#D9F0FF] p-3"><p className="text-sm font-black">{entry.eventType}</p><p className="text-xs font-semibold text-[#64748B]">{entry.resultStatus} · {formatDateTime(entry.occurredAt)} · {entry.actorName || entry.actorRole}</p><p className="mt-1 break-all font-mono text-[10px] text-[#64748B]">Correlation: {entry.correlationId}</p></div>)}</div></Panel> : null}
            </>
          ) : <Panel title="Print Preparation Detail"><p className="text-sm font-semibold text-[#64748B]">Select a Statement from the Print Queue.</p></Panel>}
        </div>
      </section>

      <section className="grid min-w-0 gap-3 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
        <div className="flex flex-wrap items-center justify-between gap-3"><div><p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Operational Reporting</p><p className="text-sm font-bold">Print outcomes, reprints, exceptions, and historical-version activity</p></div>{permissions.canExportReport ? <button type="button" onClick={() => void exportReport()} className="focus-ring rounded-full border border-[#14B8A6] px-4 py-2 text-xs font-black uppercase tracking-[0.1em]">Export CSV</button> : null}</div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5"><Metric label="Confirmed" value={reports?.summary.confirmed || 0} /><Metric label="Cancelled" value={reports?.summary.cancelled || 0} /><Metric label="Exceptions" value={reports?.summary.failed || 0} /><Metric label="Reprints" value={reports?.summary.reprints || 0} /><Metric label="Historical versions" value={reports?.summary.historicalVersionPrints || 0} /></div>
      </section>
    </div>
  );
}

function Panel({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return <section className="min-w-0 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]"><div className="mb-3 flex items-center gap-2 text-[#0A2540]">{icon}<h2 className="font-display text-lg font-bold">{title}</h2></div>{children}</section>;
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return <div className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2.5"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#0F766E]">{label}</p><p className="mt-1 break-words text-lg font-black">{value}</p></div>;
}

function Field({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0 rounded-xl border border-[#D9F0FF] bg-[#F8FCFF] px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.1em] text-[#64748B]">{label}</p><p className="mt-1 break-words text-sm font-bold">{value}</p></div>;
}

function humanize(value: string) {
  return value.replace(/([a-z])([A-Z])/g, "$1 $2").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDate(value?: string | null) {
  return value ? new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(new Date(value)) : "Not captured";
}

function formatDateTime(value?: string | null) {
  return value ? new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "Not captured";
}

function formatMoney(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

function formatBytes(bytes?: number | null) {
  if (!bytes) return "Not captured";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
